/**
 * §7.4 Scenario Simulator — the gem of v0.1a.
 *
 * Picks: pathogen × region × intervention basket × duration.
 * Runs live simulateSeird against the engine. Renders trajectories
 * for I (infectious) and D (deaths) with the determinism receipt
 * footer so users see the underlying audit trail.
 */

import { useMemo, useState } from 'react';
import { findPathogen, listPathogens } from '@biomax/plugin-pathogen-database';
import { findRegion, listRegions } from '@biomax/plugin-region-database';
import { NPI_CATALOG, listNpiIds, composeBetaScaler } from '@biomax/plugin-epi-interventions';
import { Sparkline } from '../sparkline';
import { useScenario } from '../ScenarioContext';
import { simulateForPathogen } from '../simRouting';
import { auditCapture } from '../audit/capture';
import { PinToBriefButton } from '../pack/PinToBriefButton';

type ChartMode = 'standard' | 'counterfactual';

export function ScenarioSimulator() {
  const pathogens = listPathogens();
  const regions = listRegions();
  const npiIds = listNpiIds();

  // Shared scenario (pathogen × region × horizon) persists across surfaces.
  const { pathogenId, regionId, durationDays: duration, setPathogenId, setRegionId, setDurationDays: setDuration } = useScenario();
  const [activeNpis, setActiveNpis] = useState<string[]>([]);

  // Phase 8.2 — Counterfactual mode (CD R9 Tier-A #2). Toggles the chart
  // panel into a divergence view: baseline (no action) vs intervention,
  // shaded area = cases averted, decision-point line marked. Treated as a
  // MODE of the existing surface per CD's "new view of existing noun" rule,
  // not a new §7.x. The intervention-start day defaults to day 14 (matches
  // the NPI schedule above) and is operator-tunable.
  const [chartMode, setChartMode] = useState<ChartMode>('standard');
  const [decisionDay, setDecisionDay] = useState<number>(14);
  const handleModeSwitch = (next: ChartMode) => {
    if (next === chartMode) return;
    setChartMode(next);
    auditCapture.emit({
      event: { type: 'surface.view', surface: '§7.4', subjectIds: [], payload: { chartMode: next } },
    });
  };

  const result = useMemo(() => {
    const pathogen = findPathogen(pathogenId);
    const region = findRegion(regionId);
    if (!pathogen || !region) return null;

    // Compose βscaler from active NPIs running for the entire simulation window.
    const schedule = activeNpis.map(npiId => ({
      npiId, startDay: 14, endDay: duration - 14,
    }));
    const betaScaler = schedule.length > 0 ? composeBetaScaler(schedule, NPI_CATALOG) : undefined;

    // Routes vector-borne pathogens through the host-vector model; NPIs apply to both.
    return simulateForPathogen(
      pathogen,
      { N: region.population.total, I0: 100, r0: pathogen.R0.value, durationDays: duration, dtDays: 1 },
      betaScaler,
    );
  }, [pathogenId, regionId, duration, activeNpis]);

  const toggleNpi = (id: string) => {
    setActiveNpis(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Reference: same simulation without interventions for delta.
  const baseline = useMemo(() => {
    const pathogen = findPathogen(pathogenId);
    const region = findRegion(regionId);
    if (!pathogen || !region) return null;
    return simulateForPathogen(
      pathogen,
      { N: region.population.total, I0: 100, r0: pathogen.R0.value, durationDays: duration, dtDays: 1 },
    );
  }, [pathogenId, regionId, duration]);

  if (!result || !baseline) return <div>Loading…</div>;

  const finalDeaths = result.compartments.D!.at(-1)!;
  const baselineDeaths = baseline.compartments.D!.at(-1)!;
  const peakI = Math.max(...result.compartments.I!);
  const peakIDay = result.compartments.I!.indexOf(peakI);
  const dalys = finalDeaths * 20; // crude — actual DALY tool in §7.6 / economic plugin
  const region = findRegion(regionId);
  const pathogen = findPathogen(pathogenId);

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.4</span>
        <h1 className="surface__title">Scenario Simulator</h1>
      </div>
      <div className="surface__sub" style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
      }}>
        <span>
          Live engine simulation · catalog parameters · deterministic RK4 ·
          {' '}<span className="receipt"><span className="receipt__dot" />engine {result.receipt.engineVersion} · seed {result.receipt.seed} · {result.receipt.elapsedMs.toFixed(0)} ms · {result.receipt.steps} steps</span>
        </span>
        <span style={{ flex: 1 }} />
        <PinToBriefButton item={{
          id: `scenario-${pathogenId}-${regionId}-${activeNpis.join('-') || 'no-npi'}-d${duration}`,
          surface: '§7.4',
          label: `${pathogen!.names.common} × ${region!.names.english} · ${Math.round(finalDeaths).toLocaleString()} deaths${activeNpis.length > 0 ? ` (NPIs: ${activeNpis.join('+')})` : ' (baseline)'}`,
          value: Math.round(finalDeaths),
          context: {
            pathogen: pathogenId, region: regionId, duration,
            activeNpis, chartMode, decisionDay,
            finalDeaths: Math.round(finalDeaths),
            baselineDeaths: Math.round(baselineDeaths),
            peakI, peakIDay,
          },
        }} />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Scenario configuration</span>
        </div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Pathogen</span>
            <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
              {pathogens.map(p => <option key={p.id} value={p.id}>{p.names.common}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Region</span>
            <select value={regionId} onChange={e => setRegionId(e.target.value)}>
              {regions
                .slice()
                .sort((a, b) => a.names.english.localeCompare(b.names.english))
                .map(r => <option key={r.id} value={r.id}>{r.names.english}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Duration (days)</span>
            <input type="number" value={duration} min={30} max={730}
              onChange={e => setDuration(parseInt(e.target.value, 10) || 180)} />
          </label>
        </div>
        <div style={{ marginTop: 16 }}>
          <span className="simctl__label">NPI basket · click to toggle</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--bm-space-2)', marginTop: 8 }}>
            {npiIds.map(id => {
              const active = activeNpis.includes(id);
              const spec = NPI_CATALOG[id]!;
              return (
                <button
                  key={id}
                  className={active ? 'btn btn--primary' : 'btn'}
                  onClick={() => toggleNpi(id)}
                  title={`βscale ${spec.betaScale}`}
                >
                  {spec.label} · ×{spec.betaScale}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="kpi__label">Projected deaths</div>
          <div className="kpi__value kpi__value--danger">{Math.round(finalDeaths).toLocaleString()}</div>
          <div className="kpi__sub">
            {finalDeaths < baselineDeaths
              ? `↓ ${Math.round(baselineDeaths - finalDeaths).toLocaleString()} vs no intervention`
              : 'baseline (no intervention)'}
          </div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Peak infectious</div>
          <div className="kpi__value kpi__value--signal">{(peakI / 1e6).toFixed(2)}M</div>
          <div className="kpi__sub">day {peakIDay}</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Cumulative infected</div>
          <div className="kpi__value">{((region!.population.total - result.compartments.S!.at(-1)!) / 1e6).toFixed(0)}M</div>
          <div className="kpi__sub">{
            (((region!.population.total - result.compartments.S!.at(-1)!) / region!.population.total) * 100).toFixed(1)
          }% of pop</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">DALYs (crude)</div>
          <div className="kpi__value">{(dalys / 1e3).toFixed(0)}k</div>
          <div className="kpi__sub">YLL only · 20 yrs/death proxy</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">R0 used</div>
          <div className="kpi__value">{pathogen!.R0.value.toFixed(1)}</div>
          <div className="kpi__sub">catalog central</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="panel__title">Trajectories</span>
          <span className="panel__meta">infectious compartment vs baseline</span>
          <span style={{ flex: 1 }} />
          <ModeToggle mode={chartMode} setMode={handleModeSwitch} />
        </div>
        {chartMode === 'standard' ? (
          <>
            <Sparkline values={result.compartments.I!} label="With NPIs (I)" stroke="var(--bm-accent)" />
            <Sparkline values={baseline.compartments.I!} label="No interventions (I_base)" stroke="var(--bm-danger)" />
          </>
        ) : (
          <CounterfactualDivergence
            baseline={baseline.compartments.I!}
            intervention={result.compartments.I!}
            decisionDay={decisionDay}
            setDecisionDay={setDecisionDay}
            duration={duration}
          />
        )}
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Cumulative deaths</span>
          <span className="panel__meta">D compartment</span>
        </div>
        <Sparkline values={result.compartments.D!} label="With NPIs (D)" stroke="var(--bm-accent)" />
        <Sparkline values={baseline.compartments.D!} label="No interventions (D_base)" stroke="var(--bm-danger)" />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Why this matters</span>
        </div>
        {chartMode === 'counterfactual' && (
          <div style={{
            margin: '0 var(--bm-space-3) var(--bm-space-3)',
            padding: '10px 13px',
            fontSize: 12.5, color: 'var(--bm-text-muted)', lineHeight: 1.5,
            background: 'var(--bm-bg-subtle)',
            border: '1px solid var(--bm-border)',
            borderLeft: '3px solid var(--bm-prov-synthetic, var(--bm-state-stale))',
            borderRadius: 7,
          }}>
            <b style={{ color: 'var(--bm-text)' }}>◇ Synthetic model output.</b>
            {' '}Both trajectories are model draws, not observations. The divergence is the modelled
            effect of acting at the decision point — sensitive to assumptions; pair with the
            ensemble's uncertainty (§7.4b) before any briefing claim.
          </div>
        )}
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--bm-text)' }}>
          This simulator pulls <strong>{pathogen!.names.common}</strong> R0 + generation interval + IFR
          directly from the verified pathogen catalog (PMID round-trip status:{' '}
          <strong>{pathogen!.verification.status}</strong>). NPI β-scaling pulls from the
          intervention catalog with PMID citations on every entry. The output is reproducible
          to last digit on the same input (engine vX · seed · ms · steps receipt). Use this for
          scenario comparison before committing to an intervention basket via §7.5 Intervention Planner.
        </div>
      </div>
    </div>
  );
}

/** Phase 8.2 — Counterfactual mode toggle. Switches the trajectory chart
 *  between standard (two overlaid lines) and counterfactual (divergence-
 *  area visualisation) modes. */
function ModeToggle({ mode, setMode }: { mode: ChartMode; setMode: (m: ChartMode) => void }) {
  return (
    <div style={{
      display: 'inline-flex', border: '1px solid var(--bm-border)', borderRadius: 7,
      overflow: 'hidden', background: 'var(--bm-bg-subtle)',
    }}>
      {([['standard', 'Standard'], ['counterfactual', 'Counterfactual']] as const).map(([k, l]) => (
        <button
          key={k}
          type="button"
          onClick={() => setMode(k)}
          style={{
            padding: '6px 12px', border: 'none', cursor: 'pointer',
            fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 600,
            background: mode === k ? 'var(--bm-accent, #6ea3ff)' : 'transparent',
            color: mode === k ? '#fff' : 'var(--bm-text-muted)',
          }}
        >{l}</button>
      ))}
    </div>
  );
}

/** Phase 8.2 — Counterfactual divergence chart (CD R9 Tier-A #2).
 *  Renders baseline (no-action) vs intervention trajectories on a shared
 *  axis with:
 *   - shaded area between them = cases/lives averted
 *   - decision-point line marked at the intervention-start day
 *   - "≈ N averted" headline KPI
 *
 *  Honesty rail: both trajectories are model draws, not observations.
 *  Operators must pair with the ensemble's uncertainty (§7.4b) before
 *  claiming the divergence in a briefing. */
function CounterfactualDivergence({
  baseline, intervention, decisionDay, setDecisionDay, duration,
}: {
  baseline: readonly number[];
  intervention: readonly number[];
  decisionDay: number;
  setDecisionDay: (d: number) => void;
  duration: number;
}) {
  // Scale: chart-space coordinates. Layout matches R9 design proportions.
  const W = 720, H = 280, padL = 56, padR = 16, padT = 16, padB = 40;
  const pw = W - padL - padR, ph = H - padT - padB;
  const n = baseline.length;
  const max = Math.max(...baseline, 1);  // baseline is the dominant trajectory
  const X = (i: number) => padL + (i / Math.max(1, n - 1)) * pw;
  const Y = (v: number) => padT + ph - (v / max) * ph;
  const line = (a: readonly number[]) =>
    a.map((v, i) => `${i === 0 ? 'M' : 'L'} ${X(i)} ${Y(v)}`).join(' ');
  // Divergence polygon: baseline path forward, intervention path reversed back.
  const divArea =
    baseline.map((v, i) => `${i === 0 ? 'M' : 'L'} ${X(i)} ${Y(v)}`).join(' ')
    + ' ' + intervention.map((_, i) => {
      const j = n - 1 - i;
      return `L ${X(j)} ${Y(intervention[j]!)}`;
    }).join(' ')
    + ' Z';
  // Averted = cumulative difference, scaled to "k cases averted" headline.
  const averted = Math.round(
    (baseline.reduce((a, b) => a + b, 0) - intervention.reduce((a, b) => a + b, 0)) / 1000,
  );
  const abbr = (v: number): string =>
    v >= 1e6 ? (v / 1e6).toFixed(1) + 'M'
    : v >= 1e3 ? (v / 1e3).toFixed(0) + 'k'
    : Math.round(v).toString();
  return (
    <div style={{ padding: 'var(--bm-space-2) var(--bm-space-3) var(--bm-space-3)' }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap', marginBottom: 12,
      }}>
        <span style={{ fontSize: 13, color: 'var(--bm-text-muted)' }}>
          ≈ <b style={{
            fontFamily: 'var(--bm-font-mono)', fontSize: 17,
            color: 'var(--bm-prov-confirmed, var(--bm-accent))',
          }}>{abbr(averted * 1000)}</b> cases averted vs no-action
        </span>
        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, color: 'var(--bm-text-muted)',
        }}>
          Decision day
          <input
            type="number" value={decisionDay} min={0} max={Math.max(0, duration - 1)}
            onChange={e => setDecisionDay(parseInt(e.target.value, 10) || 0)}
            style={{ width: 70, padding: '2px 6px' }}
          />
        </label>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }} role="img" aria-label="Counterfactual divergence chart">
        {[0, 0.25, 0.5, 0.75, 1].map((g, k) => (
          <line key={k} x1={padL} x2={W - padR} y1={padT + ph * g} y2={padT + ph * g}
            stroke="var(--bm-border)" strokeWidth={0.5} />
        ))}
        <path d={divArea} fill="var(--bm-prov-confirmed, var(--bm-accent))" opacity={0.13} />
        <path d={line(baseline)} fill="none" stroke="var(--bm-danger, var(--bm-threat-1))" strokeWidth={2.4} />
        <path d={line(intervention)} fill="none" stroke="var(--bm-prov-confirmed, var(--bm-accent))" strokeWidth={2.4} />
        <line x1={X(decisionDay)} x2={X(decisionDay)} y1={padT} y2={padT + ph}
          stroke="var(--bm-accent, #6ea3ff)" strokeDasharray="4 3" />
        <circle cx={X(decisionDay)} cy={padT + 4} r={3} fill="var(--bm-accent, #6ea3ff)" />
        <text x={X(decisionDay) + 6} y={padT + 14}
          fontFamily="var(--bm-font-mono)" fontSize={10} fill="var(--bm-accent, #6ea3ff)">decision point</text>
        <text x={padL} y={H - 8}
          fontFamily="var(--bm-font-mono)" fontSize={10} fill="var(--bm-text-subtle)">day 0</text>
        <text x={W - padR} y={H - 8} textAnchor="end"
          fontFamily="var(--bm-font-mono)" fontSize={10} fill="var(--bm-text-subtle)">day {duration}</text>
        <text x={padL - 8} y={Y(max) + 4} textAnchor="end"
          fontFamily="var(--bm-font-mono)" fontSize={10} fill="var(--bm-text-subtle)">{abbr(max)}</text>
      </svg>
      <div style={{
        display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 8,
        fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-muted)',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 14, height: 4, borderRadius: 2, background: 'var(--bm-danger, var(--bm-threat-1))' }} />
          No-action baseline
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 14, height: 4, borderRadius: 2, background: 'var(--bm-prov-confirmed, var(--bm-accent))' }} />
          With intervention
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 14, height: 4, borderRadius: 2, background: 'var(--bm-prov-confirmed, var(--bm-accent))', opacity: 0.3 }} />
          Divergence = averted
        </span>
      </div>
    </div>
  );
}
